import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import { AlertTriangle, Filter } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { DOT } from "../components/ui/status-badge";
import { getStatusTone, getStatusHex } from "../lib/statusTokens";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import { PageHeader } from "../components/PageHeader";

const RiskHeatMap = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/risks/heatmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const levelData = data?.by_level ? Object.entries(data.by_level).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: getStatusHex("risk", k)
  })).filter(d => d.value > 0) : [];

  const treatmentData = data?.by_treatment ? Object.entries(data.by_treatment).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), value: v
  })) : [];

  const categoryData = data?.by_category ? Object.entries(data.by_category).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), ...v
  })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="risk-heatmap-page">
      <PageHeader
        title={t("heatmap.title")}
        description={t("heatmap.subtitle")}
        actions={<Badge variant="outline" className="text-sm">{data?.total_risks || 0} {t("ux.total_risks")}</Badge>}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        {["critical", "high", "medium", "low"].map(level => (
          <Card key={level} className="border-border">
            <CardContent className="pt-4 pb-4 text-center">
              <p className={`text-3xl font-heading font-bold`} style={{ color: getStatusHex("risk", level) }}>
                {data?.by_level?.[level] || 0}
              </p>
              <p className="text-xs text-muted-foreground capitalize">{level}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Heat Map Matrix */}
        <div className="lg:col-span-2">
          <Card className="border-border" data-testid="heatmap-matrix-card">
            <CardHeader>
              <CardTitle className="text-lg">{t("ux.likelihood_vs_impact")}</CardTitle>
              <CardDescription>{t("ux.click_cell_hint")}</CardDescription>
            </CardHeader>
            <CardContent className="overflow-hidden">
              <div className="flex gap-2 overflow-hidden">
                {/* Y-axis vertical label — strictly contained: fixed-width parent
                    is overflow:hidden + relative; the label is positioned
                    absolutely inside it so the rotated text can't push the
                    layout out to the sidebar. */}
                <div
                  className="relative overflow-hidden flex-shrink-0"
                  style={{ width: "1.5rem" }}
                  aria-hidden="false"
                >
                  <span
                    className="text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%) rotate(-90deg)",
                      transformOrigin: "center center",
                    }}
                  >
                    {t("ux.likelihood")}
                  </span>
                </div>

                {/* Matrix grid */}
                <div className="flex-1 min-w-0 space-y-1">
                  {(data?.matrix || []).map((row, rowIdx) => (
                    <div key={rowIdx} className="flex items-center gap-1">
                      <div className="w-6 text-xs text-muted-foreground text-right flex-shrink-0">{5 - rowIdx}</div>
                      {row.map((cell, colIdx) => {
                        const isSelected = selectedCell?.likelihood === cell.likelihood && selectedCell?.impact === cell.impact;
                        return (
                          <motion.div key={colIdx}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`flex-1 h-16 rounded-lg cursor-pointer flex items-center justify-center transition-all border-2 ${
                              isSelected ? 'border-white shadow-lg' : 'border-transparent'
                            } ${DOT[getStatusTone("risk", cell.level)]} ${cell.count > 0 ? 'opacity-100' : 'opacity-40'}`}
                            onClick={() => setSelectedCell(cell.count > 0 ? cell : null)}
                            data-testid={`heatmap-cell-${cell.likelihood}-${cell.impact}`}
                          >
                            {cell.count > 0 && (
                              <span className="text-white font-bold text-lg">{cell.count}</span>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  ))}
                  {/* X-axis numbers */}
                  <div className="flex items-center gap-1 pt-1">
                    <div className="w-6 flex-shrink-0" />
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="flex-1 text-center text-xs text-muted-foreground">{i}</div>
                    ))}
                  </div>
                  {/* X-axis label */}
                  <div className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wider mt-1">
                    {t("ux.impact")}
                  </div>
                </div>
              </div>

              {/* Selected cell detail */}
              {selectedCell && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-4 rounded-lg border border-border bg-muted/30">
                  <p className="text-sm font-medium mb-2">
                    L:{selectedCell.likelihood} x I:{selectedCell.impact} = Score {selectedCell.score} ({selectedCell.level})
                  </p>
                  <div className="space-y-2">
                    {selectedCell.risks?.map(r => (
                      <div key={r.risk_id} className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusHex("risk", selectedCell.level) }} />
                        <span className="flex-1">{r.title}</span>
                        {r.treatment && <Badge variant="secondary" className="text-xs capitalize">{r.treatment}</Badge>}
                        {r.risk_category && <Badge variant="outline" className="text-xs capitalize">{r.risk_category}</Badge>}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="space-y-4">
          <Card className="border-border" data-testid="risk-by-level-chart">
            <CardHeader className="pb-2"><CardTitle className="text-sm">By Risk Level</CardTitle></CardHeader>
            <CardContent>
              {levelData.length > 0 ? (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={levelData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value" paddingAngle={2}>
                        {levelData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No risks</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="risk-by-treatment-chart">
            <CardHeader className="pb-2"><CardTitle className="text-sm">By Treatment</CardTitle></CardHeader>
            <CardContent>
              {treatmentData.length > 0 ? (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={treatmentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No risks</p>
              )}
            </CardContent>
          </Card>

          {/* Category breakdown */}
          <Card className="border-border" data-testid="risk-by-category">
            <CardHeader className="pb-2"><CardTitle className="text-sm">By Category</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {categoryData.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No categorized risks</p>
              ) : (
                categoryData.map(cat => (
                  <div key={cat.name} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{cat.name}</span>
                    <div className="flex items-center gap-1">
                      {cat.critical > 0 && <Badge className={`${DOT.critical} text-white text-[10px] h-5`}>{cat.critical}</Badge>}
                      {cat.high > 0 && <Badge className={`${DOT.high} text-white text-[10px] h-5`}>{cat.high}</Badge>}
                      {cat.medium > 0 && <Badge className={`${DOT.medium} text-white text-[10px] h-5`}>{cat.medium}</Badge>}
                      {cat.low > 0 && <Badge className={`${DOT.low} text-white text-[10px] h-5`}>{cat.low}</Badge>}
                      <span className="text-muted-foreground ml-1">{cat.total}</span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RiskHeatMap;
